import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Button, CircularProgress, IconButton } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import React from 'react';
import { STUDENT_BLUE as BLUE, STUDENT_RING as RING, studentSecondaryButtonSx } from '../style/verbaScholaStudentStyles';

/** Presentation only: playback ownership and completion remain with the activity. */
export default function StudentPlaybackButton({ label, playing, loading = false, required, disabled, onClick, size = 44 }: {
    label: string; playing: boolean; loading?: boolean; required?: boolean; disabled: boolean; onClick: () => void; size?: number;
}) {
    return <IconButton aria-label={label} aria-busy={loading} aria-disabled={disabled || playing || loading}
        disabled={disabled && !playing && !loading} onClick={() => { if (!disabled && !playing && !loading) onClick(); }}
        sx={{ width: Math.max(44, size), height: Math.max(44, size), flexShrink: 0, color: BLUE, border: `1px solid ${required ? BLUE : RING}`,
            bgcolor: playing ? `${BLUE}12` : undefined,
            animation: required && !disabled && !playing && !loading ? 'listenCue 1.6s ease-out infinite' : undefined,
            '@keyframes listenCue': { from: { boxShadow: `0 0 0 0 ${BLUE}73` }, to: { boxShadow: `0 0 0 10px ${BLUE}00` } },
            '@keyframes speakingPulse': { '50%': { opacity: 0.5 } },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', '& *': { animation: 'none !important', transition: 'none !important' } } }}>
        {loading ? <CircularProgress size={22} /> : playing ? <VolumeUpRoundedIcon sx={{ animation: 'speakingPulse 1s ease-in-out infinite' }} /> : <PlayArrowRoundedIcon />}
    </IconButton>;
}

/**
 * The learner's own saved take: one labelled control in every activity (style guide: a saved
 * take needs a labelled playback control). Model audio keeps the round icon button above.
 */
export function StudentRecordingPlayButton({ playing, loading = false, disabled = false, onClick, label = 'Play your recording', subject, fullWidth = false, sx }: {
    playing: boolean; loading?: boolean; disabled?: boolean; onClick: () => void;
    /** Visible label while stopped, for example a clip's start time. */
    label?: string;
    /** Names what is played when the visible label alone is ambiguous, for example “recording for 你好”. */
    subject?: string;
    fullWidth?: boolean; sx?: SxProps<Theme>;
}) {
    const visible = loading ? 'Loading recording…' : playing ? 'Stop playback' : label;
    const spoken = subject ? `${loading ? 'Loading' : playing ? 'Stop playback of' : 'Play'} ${subject}` : undefined;
    return <Button aria-label={spoken} disabled={disabled} onClick={onClick} fullWidth={fullWidth}
        startIcon={loading ? <CircularProgress size={16} aria-hidden /> : playing ? <StopRoundedIcon /> : <PlayArrowRoundedIcon />}
        sx={[studentSecondaryButtonSx, ...(Array.isArray(sx) ? sx : [sx])]}>
        {visible}
    </Button>;
}
